import { createSelector } from '@reduxjs/toolkit'
import { generatePath, matchPath } from 'react-router-dom'

import {
    PATH_TAX_RETURN_REVIEW,
    PATH_TAX_RETURN_REVIEW_CAPITAL_GAINS,
    PATH_TAX_RETURN_REVIEW_DEDUCTIONS,
    PATH_TAX_RETURN_REVIEW_DIVIDENDS_AND_INTERESTS,
    PATH_TAX_RETURN_REVIEW_EMPLOYMENTS,
    PATH_TAX_RETURN_REVIEW_GENERAL,
    PATH_TAX_RETURN_REVIEW_PROPERTY_INCOMES,
    PATH_TAX_RETURN_REVIEW_SELF_EMPLOYMENTS,
    SECTION_STATUS_QUESTIONS_COMPLETED,
} from '../constants'
import { isSectionStatusAtLeast } from '../helpers'
import { getTaxReturns } from '../store/profile'

const TAX_RETURN_SECTIONS = [
    {
        hasFieldName: 'hasEmployment',
        statusFieldName: 'employmentSummaryStatus',
        path: PATH_TAX_RETURN_REVIEW_EMPLOYMENTS,
    },
    {
        hasFieldName: 'hasDividendsOrInterest',
        statusFieldName: 'dividendsAndInterestSummaryStatus',
        path: PATH_TAX_RETURN_REVIEW_DIVIDENDS_AND_INTERESTS,
    },
    {
        hasFieldName: 'hasPropertyIncome',
        statusFieldName: 'propertyIncomeSummaryStatus',
        path: PATH_TAX_RETURN_REVIEW_PROPERTY_INCOMES,
    },
    {
        hasFieldName: 'hasCapitalGains',
        statusFieldName: 'capitalGainsSummaryStatus',
        path: PATH_TAX_RETURN_REVIEW_CAPITAL_GAINS,
    },
    {
        hasFieldName: 'hasSelfEmployment',
        statusFieldName: 'selfEmploymentSummaryStatus',
        path: PATH_TAX_RETURN_REVIEW_SELF_EMPLOYMENTS,
    },
    { statusFieldName: 'generalSummaryStatus', path: PATH_TAX_RETURN_REVIEW_GENERAL },
    { statusFieldName: 'deductionSummaryStatus', path: PATH_TAX_RETURN_REVIEW_DEDUCTIONS },
]

const pluckTaxYear = (_, { taxYear }) => taxYear
export const getTaxReturnSlice = (state) => state.taxReturn
export const getTaxReturnDeductions = (state) => getTaxReturnSlice(state).deductions

export const getTaxReturnByTaxYear = createSelector([getTaxReturns, pluckTaxYear], (taxReturns, taxYear) =>
    taxReturns.find((taxReturn) => Number(taxReturn.taxYear) === Number(taxYear))
)

const getIsAnyTaxReturnSectionCompleted = createSelector(getTaxReturnSlice, (taxReturn) =>
    TAX_RETURN_SECTIONS.filter(({ hasFieldName }) => !hasFieldName || taxReturn[hasFieldName] !== false).some(
        ({ statusFieldName }) => {
            const status = taxReturn[statusFieldName]
            return isSectionStatusAtLeast(status, SECTION_STATUS_QUESTIONS_COMPLETED)
        }
    )
)

const getAreAllTaxReturnSectionsCompleted = createSelector(getTaxReturnSlice, (taxReturn) =>
    TAX_RETURN_SECTIONS.filter(({ hasFieldName }) => !hasFieldName || taxReturn[hasFieldName] !== false).every(
        ({ statusFieldName }) => {
            const status = taxReturn[statusFieldName]
            return isSectionStatusAtLeast(status, SECTION_STATUS_QUESTIONS_COMPLETED)
        }
    )
)

export const getNextUnreviewedSectionPath = ({ currentReviewPath }) =>
    createSelector(
        [getTaxReturnSlice, getAreAllTaxReturnSectionsCompleted],
        (taxReturn, areAllTaxReturnSectionsCompleted) => {
            if (areAllTaxReturnSectionsCompleted) return PATH_TAX_RETURN_REVIEW
            const ORDERED_TAX_RETURN_SECTIONS = TAX_RETURN_SECTIONS
            const { taxYear } = taxReturn

            const currentReviewSectionIndex = ORDERED_TAX_RETURN_SECTIONS.findIndex(
                ({ path }) => !!matchPath(generatePath(currentReviewPath, { taxYear }), { path })
            )
            const { path: previousUnreviewedSectionsPath } =
                ORDERED_TAX_RETURN_SECTIONS.slice(0, Math.max(0, currentReviewSectionIndex)).find(
                    ({ hasFieldName, statusFieldName }) => {
                        const needsReview = !hasFieldName || taxReturn[hasFieldName] !== false
                        const status = taxReturn[statusFieldName]
                        const isReviewed = isSectionStatusAtLeast(status, SECTION_STATUS_QUESTIONS_COMPLETED)

                        return needsReview && !isReviewed
                    }
                ) || {}

            const { path: nextUnreviewedSectionsPath } =
                ORDERED_TAX_RETURN_SECTIONS.slice(currentReviewSectionIndex + 1).find(
                    ({ hasFieldName, statusFieldName }) => {
                        const needsReview = !hasFieldName || taxReturn[hasFieldName] !== false
                        const status = taxReturn[statusFieldName]
                        const isReviewed = isSectionStatusAtLeast(status, SECTION_STATUS_QUESTIONS_COMPLETED)

                        return needsReview && !isReviewed
                    }
                ) || {}

            const nextSectionPath =
                nextUnreviewedSectionsPath || previousUnreviewedSectionsPath || PATH_TAX_RETURN_REVIEW

            return nextSectionPath
        }
    )

export default createSelector(
    [getTaxReturnSlice, getAreAllTaxReturnSectionsCompleted, getIsAnyTaxReturnSectionCompleted],
    (taxReturn, areAllTaxReturnSectionsCompleted, isAnyTaxReturnSectionCompleted) => {
        const { taxDue } = taxReturn
        const hasTaxDue = taxDue > 0

        const taxRefund = taxDue < 0 ? Math.abs(taxDue) : null
        const hasTaxRefund = taxRefund > 0

        const hasNoTaxDue = taxDue === 0

        const hasFinalTaxCalculation = taxDue != null

        return {
            areAllTaxReturnSectionsCompleted,
            hasFinalTaxCalculation,
            hasNoTaxDue,
            hasTaxDue,
            hasTaxRefund,
            isAnyTaxReturnSectionCompleted,
            taxRefund,
            ...taxReturn,
        }
    }
)
